/**
 * Layer Builder Functions
 *
 * Friendly constructor functions that provide:
 * - TypeScript autocomplete and type safety
 * - Clear intent (layer.vector vs layer.geojson)
 * - Automatic 'kind' discriminator injection
 */

import type {
  ClusteredVectorLayerSpec,
  CustomPinsSpec,
  GeoJsonLayerSpec,
  LayerSpec,
  RasterLayerSpec,
  VectorLayerSpec,
} from "./types";

/**
 * Layer builder namespace - use like:
 *
 * @example
 * const myLayer = layer.vector({
 *   id: 'feeders',
 *   tileset: 'mapbox://texture.feeders',
 *   sourceLayer: 'feeders',
 *   renderAs: 'line',
 *   style: { color: { token: 'brand-primary' } }
 * });
 */
export const layer = {
  /**
   * Create a vector tile layer
   *
   * Vector tiles are efficient for large datasets served from Mapbox or
   * custom tile servers. Use this for infrastructure layers, boundaries, etc.
   */
  vector: (config: Omit<VectorLayerSpec, "kind">): VectorLayerSpec => ({
    kind: "vector",
    ...config,
  }),

  /**
   * Create a GeoJSON layer
   *
   * GeoJSON is great for smaller datasets or data that changes frequently.
   * Can load from an object or URL. Supports client-side clustering.
   */
  geojson: (config: Omit<GeoJsonLayerSpec, "kind">): GeoJsonLayerSpec => ({
    kind: "geojson",
    ...config,
  }),

  /**
   * Create a custom markers layer with React components
   *
   * Use this when you need fully custom UI for markers (cards, badges, etc.)
   * Good for small numbers of markers. For many markers, consider vector tiles.
   */
  customPins: <T = any>(config: Omit<CustomPinsSpec<T>, "kind">): CustomPinsSpec<T> => ({
    kind: "customPins",
    ...config,
  }),

  /**
   * Create a raster layer from image tiles
   *
   * Use this for satellite imagery, weather overlays, heatmaps from tile servers, etc.
   * Raster layers display images rather than vector data.
   */
  raster: (config: Omit<RasterLayerSpec, "kind">): RasterLayerSpec => ({
    kind: "raster",
    ...config,
  }),

  /**
   * Create a clustered vector tile layer
   *
   * Use this for server-side clustered MVT tiles with large numbers of point features.
   * The backend service pre-clusters points and provides cluster metadata.
   * Automatically handles cluster styling, tooltips, and zoom interactions.
   */
  clusteredVector: (config: Omit<ClusteredVectorLayerSpec, "kind">): ClusteredVectorLayerSpec => ({
    kind: "clusteredVector",
    ...config,
  }),
};

/**
 * Type guard to check if a layer is a vector layer
 */
export function isVectorLayer(layer: LayerSpec): layer is VectorLayerSpec {
  return layer.kind === "vector";
}

/**
 * Type guard to check if a layer is a GeoJSON layer
 */
export function isGeoJsonLayer(layer: LayerSpec): layer is GeoJsonLayerSpec {
  return layer.kind === "geojson";
}

/**
 * Type guard to check if a layer is a custom pins layer
 */
export function isCustomPinsLayer(layer: LayerSpec): layer is CustomPinsSpec {
  return layer.kind === "customPins";
}

/**
 * Type guard to check if a layer is a raster layer
 */
export function isRasterLayer(layer: LayerSpec): layer is RasterLayerSpec {
  return layer.kind === "raster";
}

/**
 * Type guard to check if a layer is a clustered vector layer
 */
export function isClusteredVectorLayer(layer: LayerSpec): layer is ClusteredVectorLayerSpec {
  return layer.kind === "clusteredVector";
}
